import { IconAlertCircle, IconEdit, IconFeather, IconRestore, IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import mermaid from 'mermaid';



import React, { useCallback, useEffect, useRef, useState } from 'react';



import { ActionIcon, Alert, Box, Button, Center, Group, Loader, Text } from '@mantine/core';
import { useComputedColorScheme } from '@mantine/core';



import { AiMermaidGeneratorModal } from './AiMermaidGeneratorModal';


interface MermaidRendererProps {
    chart: string;
    height?: string | number;
    width?: string | number;
    onAiGenerate?: (mermaidCode: string) => void;
    onEdit?: () => void;
}

const MermaidRenderer: React.FC<MermaidRendererProps> = ({
    chart,
    height = 400,
    width = '100%',
    onAiGenerate,
    onEdit
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgContainerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [mermaidInitialized, setMermaidInitialized] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
    const [aiModalOpened, setAiModalOpened] = useState(false);
    const dark = useComputedColorScheme('dark') === 'dark';

    // Zoom and pan controls
    const handleZoomIn = useCallback(() => {
        setZoomLevel(prev => Math.min(prev * 1.2, 3));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoomLevel(prev => Math.max(prev / 1.2, 0.05)); // Decreased minimum zoom to 0.05 (5%)
    }, []);

    const handleResetZoom = useCallback(() => {
        setZoomLevel(1);
        setPanOffset({ x: 0, y: 0 });
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button === 0) {
            // Left mouse button
            setIsPanning(true);
            setLastPanPoint({ x: e.clientX, y: e.clientY });
        }
    }, []);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (isPanning) {
                const deltaX = e.clientX - lastPanPoint.x;
                const deltaY = e.clientY - lastPanPoint.y;
                setPanOffset(prev => ({
                    x: prev.x + deltaX,
                    y: prev.y + deltaY
                }));
                setLastPanPoint({ x: e.clientX, y: e.clientY });
            }
        },
        [isPanning, lastPanPoint]
    );

    const handleMouseUp = useCallback(() => {
        setIsPanning(false);
    }, []);

    // AI generation handlers
    const handleOpenAiModal = useCallback(() => {
        setAiModalOpened(true);
    }, []);

    const handleCloseAiModal = useCallback(() => {
        setAiModalOpened(false);
    }, []);

    const handleAiGenerate = useCallback(
        (mermaidCode: string) => {
            onAiGenerate?.(mermaidCode);
        },
        [onAiGenerate]
    );

    const onBackToEditorClick = () => {
        if (onEdit) {
            onEdit();
            setError(null);
        }
    };

    const handleNativeWheelWrapper = (e: Event) => {
        const wheelEvent = e as WheelEvent; // Safe cast
        // Completely block all wheel events on this container
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Only allow zoom functionality
        const delta = wheelEvent.deltaY > 0 ? 0.9 : 1.1;
        setZoomLevel(prev => Math.max(0.05, Math.min(3, prev * delta)));
    };

    // Initialize Mermaid once
    useEffect(() => {
        if (!mermaidInitialized) {
            mermaid.initialize({
                startOnLoad: false,
                theme: dark ? 'dark' : 'default',
                themeVariables: {
                    primaryColor: '#339af0',
                    primaryTextColor: dark ? '#ffffff' : '#000000',
                    primaryBorderColor: '#339af0',
                    lineColor: dark ? '#868e96' : '#495057',
                    sectionBkgColor: dark ? '#343a40' : '#f8f9fa',
                    altSectionBkgColor: dark ? '#495057' : '#e9ecef',
                    gridColor: dark ? '#6c757d' : '#adb5bd',
                    secondaryColor: '#51cf66',
                    tertiaryColor: '#ffd43b'
                },
                securityLevel: 'loose',
                gantt: {
                    numberSectionStyles: 4,
                    axisFormat: '%m/%d/%Y',
                    tickInterval: '1week',
                    gridLineStartPadding: 350,
                    fontSize: 11,
                    // @ts-expect-error - fontFamily is supported but not in types
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    sectionFontSize: 24,
                    bottomPadding: 25,
                    leftPadding: 75,
                    topPadding: 50,
                    rightPadding: 75
                }
            });
            setMermaidInitialized(true);
        }
    }, [dark, mermaidInitialized]);

    // Re-initialize when theme changes
    useEffect(() => {
        if (mermaidInitialized) {
            mermaid.initialize({
                startOnLoad: false,
                theme: dark ? 'dark' : 'default',
                themeVariables: {
                    primaryColor: '#339af0',
                    primaryTextColor: dark ? '#ffffff' : '#000000',
                    primaryBorderColor: '#339af0',
                    lineColor: dark ? '#868e96' : '#495057',
                    sectionBkgColor: dark ? '#343a40' : '#f8f9fa',
                    altSectionBkgColor: dark ? '#495057' : '#e9ecef',
                    gridColor: dark ? '#6c757d' : '#adb5bd',
                    secondaryColor: '#51cf66',
                    tertiaryColor: '#ffd43b'
                },
                securityLevel: 'loose',
                gantt: {
                    numberSectionStyles: 4,
                    axisFormat: '%m/%d/%Y',
                    tickInterval: '1week',
                    gridLineStartPadding: 350,
                    fontSize: 11,
                    // @ts-expect-error - fontFamily is supported but not in types
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    sectionFontSize: 24,
                    bottomPadding: 25,
                    leftPadding: 75,
                    topPadding: 50,
                    rightPadding: 75
                }
            });
        }
    }, [dark, mermaidInitialized]);

    // Add comprehensive wheel event handling to prevent all scrolling except zoom
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Add multiple event listeners to catch all possible wheel events
        container.addEventListener('wheel', handleNativeWheelWrapper, {
            passive: false,
            capture: true
        });
        container.addEventListener('mousewheel', handleNativeWheelWrapper, {
            passive: false,
            capture: true
        });
        container.addEventListener('DOMMouseScroll', handleNativeWheelWrapper, {
            passive: false,
            capture: true
        });

        // Prevent scrolling on the container itself
        container.style.overflow = 'hidden';
        container.style.scrollBehavior = 'auto';
        // Hide scrollbars completely
        container.style.setProperty('-webkit-overflow-scrolling', 'touch');
        container.style.setProperty('scrollbar-width', 'none');
        container.style.setProperty('-ms-overflow-style', 'none');

        // Add CSS rule to hide webkit scrollbars
        const style = document.createElement('style');
        style.textContent = `
            .mermaid-container::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
                background: transparent !important;
            }
        `;
        document.head.appendChild(style);
        container.classList.add('mermaid-container');

        return () => {
            container.removeEventListener('wheel', handleNativeWheelWrapper, {
                capture: true
            });
            container.removeEventListener('mousewheel', handleNativeWheelWrapper, {
                capture: true
            });
            container.removeEventListener('DOMMouseScroll', handleNativeWheelWrapper, {
                capture: true
            });
            container.classList.remove('mermaid-container');
            document.head.removeChild(style);
        };
    }, []);

    // Render chart when chart content or theme changes
    useEffect(() => {
        const renderChart = async () => {
            if (!chart.trim() || !mermaidInitialized || !containerRef.current) {
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Clear previous content safely
                if (svgContainerRef.current) {
                    // Remove all child nodes safely
                    while (svgContainerRef.current.firstChild) {
                        svgContainerRef.current.removeChild(
                            svgContainerRef.current.firstChild
                        );
                    }
                }

                // Generate unique ID for this chart
                const chartId = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                // Validate and render the chart
                const isValid = await mermaid.parse(chart);
                if (!isValid) {
                    throw new Error('Invalid Mermaid syntax');
                }

                const { svg } = await mermaid.render(chartId, chart);

                if (svgContainerRef.current) {
                    // Create a temporary div to hold the SVG
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = svg;

                    // Move all nodes from temp div to container
                    while (tempDiv.firstChild) {
                        svgContainerRef.current.appendChild(tempDiv.firstChild);
                    }

                    // Find the SVG element and set responsive attributes
                    const svgElement =
                        svgContainerRef.current.querySelector('svg');
                    if (svgElement) {
                        svgElement.style.maxWidth = 'none';
                        svgElement.style.height = 'auto';
                        svgElement.setAttribute(
                            'preserveAspectRatio',
                            'xMidYMid meet'
                        );
                        // Prevent dragging of the SVG itself
                        svgElement.style.userSelect = 'none';
                        svgElement.style.pointerEvents = 'none';
                    }
                }
            } catch (err) {
                console.error('Mermaid rendering error:', err);
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to render diagram'
                );
            } finally {
                setIsLoading(false);
            }
        };

        void renderChart();
    }, [chart, mermaidInitialized, dark]);

    if (!chart.trim()) {
        return (
            <Center h={height} style={{ width }}>
                <Text c='dimmed'>No diagram to display</Text>
            </Center>
        );
    }

    if (error) {
        return (
            <Alert
                icon={<IconAlertCircle size={16} />}
                title='Diagram Error'
                color='red'
                style={{ height, width }}
            >
                <Text size='sm'>{error}</Text>
                <Text size='xs' c='dimmed' mt='xs'>
                    Please check your Mermaid syntax and try again.
                </Text>
                <Button variant='light' size='xs' onClick={onBackToEditorClick}>
                    Go back to editor
                </Button>
            </Alert>
        );
    }

    return (
        <Box style={{ position: 'relative', width, height }}>
            {/* Zoom Controls */}
            <Group
                gap='xs'
                style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    zIndex: 10,
                    backgroundColor:
                        'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
                    padding: '4px',
                    borderRadius: 'var(--mantine-radius-sm)',
                    border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
                    boxShadow: 'var(--mantine-shadow-sm)'
                }}
            >
                {onEdit && (
                    <ActionIcon
                        size='sm'
                        variant='subtle'
                        onClick={onEdit}
                        title='Edit'
                    >
                        <IconEdit size={14} />
                    </ActionIcon>
                )}
                {onAiGenerate && (
                    <ActionIcon
                        size='sm'
                        variant='subtle'
                        onClick={handleOpenAiModal}
                        title='Generate with AI'
                    >
                        <IconFeather size={14} />
                    </ActionIcon>
                )}
                <ActionIcon
                    size='sm'
                    variant='subtle'
                    onClick={handleZoomIn}
                    title='Zoom In'
                >
                    <IconZoomIn size={14} />
                </ActionIcon>
                <ActionIcon
                    size='sm'
                    variant='subtle'
                    onClick={handleZoomOut}
                    title='Zoom Out'
                >
                    <IconZoomOut size={14} />
                </ActionIcon>
                <ActionIcon
                    size='sm'
                    variant='subtle'
                    onClick={handleResetZoom}
                    title='Reset Zoom'
                >
                    <IconRestore size={14} />
                </ActionIcon>
                <Text
                    size='xs'
                    c='dimmed'
                    style={{ minWidth: '40px', textAlign: 'center' }}
                >
                    {Math.round(zoomLevel * 100)}%
                </Text>
            </Group>

            {/* Main Container */}
            <Box
                ref={containerRef}
                style={{
                    height,
                    width,
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
                    borderRadius: 'var(--mantine-radius-sm)',
                    backgroundColor:
                        'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
                    cursor: isPanning ? 'grabbing' : 'grab',
                    touchAction: 'none', // Prevent touch scrolling
                    scrollbarWidth: 'none', // Firefox
                    msOverflowStyle: 'none' // IE/Edge
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {isLoading && (
                    <Center
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor:
                                'light-dark(rgba(255, 255, 255, 0.8), rgba(37, 38, 43, 0.8))',
                            zIndex: 1
                        }}
                    >
                        <Loader size='sm' />
                    </Center>
                )}
                <div
                    ref={svgContainerRef}
                    style={{
                        minHeight: '100%',
                        padding: '1rem',
                        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                        transformOrigin: 'center center',
                        transition: isPanning
                            ? 'none'
                            : 'transform 0.1s ease-out',
                        overflow: 'visible',
                        position: 'relative'
                    }}
                />
            </Box>

            {/* AI Generation Modal */}
            <AiMermaidGeneratorModal
                opened={aiModalOpened}
                onClose={handleCloseAiModal}
                onGenerate={handleAiGenerate}
            />
        </Box>
    );
};

export default MermaidRenderer;
